// Quiz History Service
// Quản lý lịch sử làm trắc nghiệm và phân tích kết quả

import { sqliteDatabaseService } from '../database/SQLiteDatabaseService';
import { syncService } from '../sync/SyncService';
import {
  QuizResult,
  CreateQuizResultData,
  QuizFilter,
  ComparisonData,
  TrendData
} from '../../types/learningHistory';

interface QuizStatistics {
  totalQuizzes: number;
  averageScore: number;
  passRate: number; // %
  totalTimeSpent: number; // seconds
  bestScore: number;
  worstScore: number;
  recentImprovement: number; // percentage points
  quizzesByType: {
    lesson: number;
    random: number;
    exam: number;
    practice: number;
  };
}

class QuizHistoryService {
  private static instance: QuizHistoryService;
  
  private constructor() {}
  
  public static getInstance(): QuizHistoryService {
    if (!QuizHistoryService.instance) {
      QuizHistoryService.instance = new QuizHistoryService();
    }
    return QuizHistoryService.instance;
  }

  // ==================== SAVE QUIZ RESULTS ====================

  /**
   * Lưu kết quả bài trắc nghiệm
   */
  async saveQuizResult(data: CreateQuizResultData): Promise<QuizResult> {
    console.log(`💾 Saving quiz result: ${data.quizTitle} - Score: ${data.score}`);

    // Calculate attempt number
    const previousAttempts = await this.getAllAttempts(data.userId, data.quizId);
    const attemptNumber = previousAttempts.length + 1;

    const resultData = {
      ...data,
      attemptNumber
    };

    const result = await sqliteDatabaseService.saveQuizResult(resultData);
    
    // Add to sync queue
    syncService.addToQueue({
      type: 'quiz_result',
      action: 'create',
      data: result
    });
    
    console.log(`✅ Quiz result saved successfully. Attempt #${attemptNumber}`);
    
    return result;
  }

  // ==================== RETRIEVE HISTORY ====================

  /**
   * Lấy lịch sử làm bài với filters
   */
  async getQuizHistory(userId: string, filters?: QuizFilter): Promise<QuizResult[]> {
    console.log(`📚 Getting quiz history for user: ${userId}`, filters);

    const results = await sqliteDatabaseService.getQuizResults(userId, filters);
    
    // Apply additional filters if needed
    let filteredResults = results;

    if (filters?.minScore !== undefined) {
      filteredResults = filteredResults.filter(r => r.score >= filters.minScore!);
    }

    if (filters?.maxScore !== undefined) {
      filteredResults = filteredResults.filter(r => r.score <= filters.maxScore!);
    }

    if (filters?.startDate) {
      const startTime = new Date(filters.startDate).getTime();
      filteredResults = filteredResults.filter(r => 
        new Date(r.createdAt).getTime() >= startTime
      );
    }

    if (filters?.endDate) {
      const endTime = new Date(filters.endDate).getTime();
      filteredResults = filteredResults.filter(r => 
        new Date(r.createdAt).getTime() <= endTime
      );
    }

    // Apply sorting
    if (filters?.sortBy) {
      filteredResults = this.sortResults(filteredResults, filters.sortBy, filters.sortOrder);
    }

    // Apply limit and offset
    if (filters?.offset !== undefined) {
      filteredResults = filteredResults.slice(filters.offset);
    }

    if (filters?.limit !== undefined) {
      filteredResults = filteredResults.slice(0, filters.limit);
    }

    console.log(`✅ Found ${filteredResults.length} quiz results`);
    return filteredResults;
  }

  /**
   * Lấy tất cả lịch sử của 1 bài cụ thể
   */
  async getQuizByLesson(userId: string, lessonId: string): Promise<QuizResult[]> {
    console.log(`📝 Getting quiz history for lesson: ${lessonId}`);

    return await sqliteDatabaseService.getQuizResults(userId, {
      lessonId,
      limit: 100
    });
  }

  /**
   * Lấy lần làm bài mới nhất
   */
  async getLatestAttempt(userId: string, quizId: string): Promise<QuizResult | null> {
    const latest = await sqliteDatabaseService.getLatestQuizAttempt(userId, quizId);
    
    if (latest) {
      console.log(`📄 Latest attempt: Attempt #${latest.attemptNumber}, Score: ${latest.score}`);
    }
    
    return latest;
  }

  /**
   * Lấy tất cả lần làm bài của 1 quiz
   */
  async getAllAttempts(userId: string, quizId: string): Promise<QuizResult[]> {
    const results = await sqliteDatabaseService.getQuizResults(userId, {
      quizId
    });

    // Sort by attempt number
    results.sort((a, b) => a.attemptNumber - b.attemptNumber);

    console.log(`📊 Found ${results.length} attempts for quiz: ${quizId}`);
    return results;
  }

  // ==================== STATISTICS ====================

  /**
   * Thống kê tổng quan về quiz của user
   */
  async getQuizStatistics(userId: string): Promise<QuizStatistics> {
    console.log(`📊 Calculating quiz statistics for user: ${userId}`);

    const allQuizzes = await sqliteDatabaseService.getQuizResults(userId);

    if (allQuizzes.length === 0) {
      return {
        totalQuizzes: 0,
        averageScore: 0,
        passRate: 0,
        totalTimeSpent: 0,
        bestScore: 0,
        worstScore: 0,
        recentImprovement: 0,
        quizzesByType: {
          lesson: 0,
          random: 0,
          exam: 0,
          practice: 0
        }
      };
    }

    // Calculate statistics
    const totalQuizzes = allQuizzes.length;
    const totalScore = allQuizzes.reduce((sum, r) => sum + r.score, 0);
    const averageScore = totalScore / totalQuizzes;
    
    const passedQuizzes = allQuizzes.filter(r => r.passed).length;
    const passRate = (passedQuizzes / totalQuizzes) * 100;
    
    const totalTimeSpent = allQuizzes.reduce((sum, r) => sum + r.timeSpent, 0);
    
    const scores = allQuizzes.map(r => r.score).sort((a, b) => a - b);
    const bestScore = scores[scores.length - 1];
    const worstScore = scores[0];

    // Calculate recent improvement (last 5 vs previous 5)
    const recentImprovement = this.calculateRecentImprovement(allQuizzes);

    // Quiz type distribution
    const quizzesByType = {
      lesson: allQuizzes.filter(r => r.quizType === 'lesson').length,
      random: allQuizzes.filter(r => r.quizType === 'random').length,
      exam: allQuizzes.filter(r => r.quizType === 'exam').length,
      practice: allQuizzes.filter(r => r.quizType === 'practice').length
    };

    const stats: QuizStatistics = {
      totalQuizzes,
      averageScore,
      passRate,
      totalTimeSpent,
      bestScore,
      worstScore,
      recentImprovement,
      quizzesByType
    };

    console.log('✅ Quiz statistics calculated:', stats);
    return stats;
  }

  /**
   * Điểm trung bình (có thể lọc theo lessonId)
   */
  async getAverageScore(userId: string, lessonId?: string): Promise<number> {
    const filters: QuizFilter = lessonId ? { lessonId } : {};
    const quizzes = await this.getQuizHistory(userId, filters);

    if (quizzes.length === 0) return 0;

    const totalScore = quizzes.reduce((sum, r) => sum + r.score, 0);
    return totalScore / quizzes.length;
  }

  /**
   * Xu hướng tiến bộ của user với 1 bài cụ thể
   */
  async getImprovementTrend(userId: string, lessonId: string): Promise<TrendData> {
    console.log(`📈 Calculating improvement trend for lesson: ${lessonId}`);

    const attempts = await this.getQuizByLesson(userId, lessonId);

    if (attempts.length === 0) {
      return {
        lessonId,
        attempts: [],
        trend: 'stable',
        averageScore: 0,
        improvement: 0,
        consistency: 0
      };
    }

    // Sort by date
    attempts.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

    // Calculate average score
    const totalScore = attempts.reduce((sum, r) => sum + r.score, 0);
    const averageScore = totalScore / attempts.length;

    // Calculate improvement (first attempt vs last attempt)
    const firstScore = attempts[0].score;
    const lastScore = attempts[attempts.length - 1].score;
    const improvement = lastScore - firstScore;

    // Determine trend
    let trend: 'improving' | 'declining' | 'stable' = 'stable';
    
    if (attempts.length >= 2) {
      const recentHalf = Math.ceil(attempts.length / 2);
      const recentScores = attempts.slice(-recentHalf);
      const olderScores = attempts.slice(0, recentHalf);

      const recentAvg = recentScores.reduce((sum, r) => sum + r.score, 0) / recentScores.length;
      const olderAvg = olderScores.reduce((sum, r) => sum + r.score, 0) / olderScores.length;

      if (recentAvg > olderAvg + 5) {
        trend = 'improving';
      } else if (recentAvg < olderAvg - 5) {
        trend = 'declining';
      }
    }

    // Calculate consistency (how stable the scores are)
    const variance = this.calculateVariance(attempts.map(a => a.score));
    const consistency = Math.max(0, 100 - variance); // Higher is more consistent

    const trendData: TrendData = {
      lessonId,
      attempts,
      trend,
      averageScore,
      improvement,
      consistency
    };

    console.log('✅ Trend calculated:', { trend, improvement, consistency });
    return trendData;
  }

  // ==================== COMPARISON ====================

  /**
   * So sánh các lần làm bài của user
   */
  async compareAttempts(userId: string, quizId: string): Promise<ComparisonData> {
    console.log(`⚖️ Comparing attempts for quiz: ${quizId}`);

    const attempts = await this.getAllAttempts(userId, quizId);

    if (attempts.length === 0) {
      return {
        quizId,
        attempts: [],
        trend: 'stable',
        averageImprovement: 0,
        bestScore: 0,
        latestScore: 0
      };
    }

    // Calculate improvements between consecutive attempts
    const improvements: number[] = [];
    
    for (let i = 1; i < attempts.length; i++) {
      const improvement = attempts[i].score - attempts[i - 1].score;
      improvements.push(improvement);
    }

    const averageImprovement = improvements.length > 0
      ? improvements.reduce((sum, v) => sum + v, 0) / improvements.length
      : 0;

    // Determine trend
    let trend: 'improving' | 'declining' | 'stable' = 'stable';
    
    if (averageImprovement > 2) {
      trend = 'improving';
    } else if (averageImprovement < -2) {
      trend = 'declining';
    }

    const bestScore = Math.max(...attempts.map(a => a.score));
    const latestScore = attempts[attempts.length - 1].score;

    const comparison: ComparisonData = {
      quizId,
      attempts,
      trend,
      averageImprovement,
      bestScore,
      latestScore
    };

    console.log('✅ Comparison completed:', { trend, averageImprovement });
    return comparison;
  }

  // ==================== PRIVATE HELPERS ====================

  private sortResults(
    results: QuizResult[],
    sortBy: 'date' | 'score' | 'timeSpent',
    sortOrder: 'asc' | 'desc' = 'desc'
  ): QuizResult[] {
    const sorted = [...results];

    sorted.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'date':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case 'score':
          comparison = a.score - b.score;
          break;
        case 'timeSpent':
          comparison = a.timeSpent - b.timeSpent;
          break;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }

  private calculateRecentImprovement(quizzes: QuizResult[]): number {
    if (quizzes.length < 2) return 0;

    // Sort by date
    const sorted = [...quizzes].sort((a, b) => 
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );

    // Get last 5 and previous 5
    const take = Math.min(5, Math.floor(sorted.length / 2));
    const recent = sorted.slice(-take);
    const previous = sorted.slice(Math.max(0, sorted.length - take * 2), sorted.length - take);

    if (previous.length === 0) return 0;

    const recentAvg = recent.reduce((sum, r) => sum + r.score, 0) / recent.length;
    const previousAvg = previous.reduce((sum, r) => sum + r.score, 0) / previous.length;

    return recentAvg - previousAvg;
  }

  private calculateVariance(scores: number[]): number {
    if (scores.length === 0) return 0;

    const mean = scores.reduce((sum, s) => sum + s, 0) / scores.length;
    const squaredDiffs = scores.map(s => Math.pow(s - mean, 2));
    const variance = squaredDiffs.reduce((sum, d) => sum + d, 0) / scores.length;

    return Math.sqrt(variance); // Return standard deviation
  }
}

export const quizHistoryService = QuizHistoryService.getInstance();
export default QuizHistoryService;

